// ui/src/utils/reembolsoFormat.ts
import type { ReembolsoStatus } from '@/services/reembolsoApi';
import { CATEGORIA_LABEL as CATEGORIA_LABEL_FORM } from '@/views/Reembolso/categorias';

export type Moeda = 'BRL' | 'USD' | 'EUR' | 'GBP';
export type MoedaEstrangeira = Exclude<Moeda, 'BRL'>;
/** Cotação (para BRL) de cada moeda estrangeira presente nas despesas do dia. */
export type Cotacoes = Partial<Record<MoedaEstrangeira, number>>;

export function formatBRL(v: number | string | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
}

export function formatUSD(v: number | string | null | undefined): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v || 0));
}

/**
 * Valor na moeda da despesa, SEMPRE no formato brasileiro ("US$ 180,00", "€ 50,00"), como nos
 * canvas do lançamento (A2/A3) e da Central (B2). Antes cada moeda saía no locale nativo
 * ("$180.00"), e a mesma despesa aparecia de um jeito no painel do solicitante e de outro na
 * conferência.
 */
export function formatMoeda(v: number | string | null | undefined, moeda: Moeda = 'BRL'): string {
  if (moeda === 'BRL') return formatBRL(v);
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(Number(v || 0));
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

interface DespesaParaTotal {
  valor?: number | null;
  pedagio_valor?: number | null;
  moeda?: Moeda | null;
}

/**
 * §11.3 (generalizado no item 8b): valor de uma despesa já convertido para BRL (espelho
 * de `calcularTotais` do backend): quando há cotação > 0 para a moeda da linha, ela entra
 * convertida (`valor * cotação`); `pedagio_valor` (legado, KM) nunca converte (é sempre
 * BRL). Sem cotação para a moeda, soma numérica (degradação).
 */
export function valorDespesaBRL(d: DespesaParaTotal, cotacoes?: Cotacoes | null): number {
  const base = Number(d.valor || 0);
  const moeda = d.moeda ?? 'BRL';
  const taxa = moeda !== 'BRL' ? cotacoes?.[moeda] : null;
  const convertido = typeof taxa === 'number' && taxa > 0 ? base * taxa : base;
  return convertido + Number(d.pedagio_valor || 0);
}

/**
 * §11.3 — subtotal/total_final do formulário, iguais ao `calcularTotais` do backend
 * (moeda estrangeira convertida, `round2` com `Number.EPSILON`). Garante que o total
 * exibido bate com o `total_final` persistido/avaliado pela alçada.
 */
export function calcularTotais(
  despesas: DespesaParaTotal[],
  adiantamento: number,
  cotacoes?: Cotacoes | null,
): { subtotal: number; total_final: number } {
  const subtotal = despesas.reduce((s, d) => s + valorDespesaBRL(d, cotacoes), 0);
  const total_final = Math.max(0, subtotal - Number(adiantamento || 0));
  return { subtotal: round2(subtotal), total_final: round2(total_final) };
}

/** `AAAA-MM-DD…` → `DD/MM` (o dia da PTAX provisória); fora do formato, `null`. */
export function diaMesDeIso(dia: string | null | undefined): string | null {
  const m = String(dia ?? '').match(/^\d{4}-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[1]}` : null;
}

/** Cotação que o servidor usou no total gravado do rascunho (`cotacoes_usadas` da resposta do criar/PUT). */
export interface CotacaoUsadaTela {
  moeda: MoedaEstrangeira; cotacao: number; dia: string; provisoria: boolean; fonte: 'ptax' | 'manual';
}

/**
 * Cotações do SERVIDOR que a tela aplica no total exibido (10ª rodada do PR #459; 9ª: só as provisórias). Recebe as
 * `cotacoes_usadas` da ÚLTIMA resposta de criar/PUT do rascunho e devolve, uma por moeda (a primeira), as de moeda que:
 *  - ainda está em alguma despesa (`moedasEmUso`) — trocar USD → BRL tira a cotação na hora, sem esperar o save;
 *  - o contexto RIGOROSO do formulário (`getContextoFormulario`) NÃO trouxe — o contexto manda quando tem;
 *  - veio com cotação > 0.
 * Assim, com o contexto sem a moeda, o total exibido usa o MESMO número que o servidor gravou — provisório ou não
 * (só provisórias deixava a tela voltar a somar USD como BRL quando o servidor obtinha a PTAX rigorosa).
 */
export function cotacoesUsadasAplicaveis(
  usadas: ReadonlyArray<Partial<CotacaoUsadaTela>> | null | undefined,
  contexto: Cotacoes,
  moedasEmUso: Iterable<string | null | undefined>,
): CotacaoUsadaTela[] {
  const emUso = new Set(moedasEmUso);
  const out: CotacaoUsadaTela[] = [];
  for (const u of usadas ?? []) {
    const moeda = u.moeda;
    if (!moeda || !emUso.has(moeda) || out.some((o) => o.moeda === moeda)) continue;
    if (Number(contexto[moeda]) > 0) continue; // a cotação rigorosa do contexto manda
    const cotacao = Number(u.cotacao);
    if (!(cotacao > 0)) continue;
    out.push({ moeda, cotacao, dia: String(u.dia ?? ''), provisoria: u.provisoria === true, fonte: u.fonte === 'manual' ? 'manual' : 'ptax' });
  }
  return out;
}

/**
 * O subtotal exibido é INCERTO (10ª rodada): alguma despesa COM valor está em moeda estrangeira sem cotação no mapa
 * da tela — `calcularTotais` a soma como BRL. Mesma regra de "pesa no total" do servidor (`valor` ≠ 0; pedágio é BRL).
 */
export function subtotalIncerto(despesas: ReadonlyArray<DespesaParaTotal>, cotacoes: Cotacoes): boolean {
  return despesas.some((d) => {
    const moeda = d.moeda ?? 'BRL';
    return moeda !== 'BRL' && (Number(d.valor) || 0) !== 0 && !(Number(cotacoes[moeda]) > 0);
  });
}

/**
 * Aviso do total provisório do formulário (a fila só marca "Total provisório", por `FilaItem.total_provisorio`). Recebe as cotações JÁ aplicadas ao
 * total exibido (`cotacoesUsadasAplicaveis`) e cita só as `provisoria: true` — exatamente a PTAX de dia anterior que o
 * total na tela usa. Item sem o campo `provisoria` conta como provisório (quem chama já filtrou). `null` = sem aviso.
 */
export function textoTotalProvisorio(
  aplicadas: ReadonlyArray<{ moeda: MoedaEstrangeira; dia: string; provisoria?: boolean }> | null | undefined,
): string | null {
  const partes = (aplicadas ?? []).filter((p) => p.provisoria !== false).map((p) => {
    const dm = diaMesDeIso(p.dia);
    return dm ? `${p.moeda} de ${dm}` : p.moeda;
  });
  if (!partes.length) return null;
  const lista = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1]}`;
  return `Total provisório: PTAX de ${lista} (o BCB não respondeu). O envio recalcula com a cotação do dia.`;
}

export function formatDate(d: string | Date | null | undefined): string {
  return d ? new Date(d).toLocaleDateString('pt-BR') : '';
}

/** Extrai nome de arquivo a partir da URL/chave do comprovante. */
export function nomeArquivoComprovante(url: string | null | undefined): string {
  if (!url) return 'Comprovante';
  const raw = url.startsWith('r2:') ? url.slice(3) : url;
  try {
    const path = new URL(raw, window.location.origin).pathname;
    return decodeURIComponent(path.split('/').pop() || 'Comprovante');
  } catch {
    return raw.split('/').pop() || 'Comprovante';
  }
}

export const STATUS_LABEL: Record<ReembolsoStatus, string> = {
  rascunho: 'Rascunho',
  conferencia: 'Em conferência',
  novo: 'Novo',
  em_analise: 'Em análise',
  aprovado: 'Aprovado',
  pago: 'Pago',
  reprovado: 'Reprovado',
  cancelado: 'Cancelado',
};

/**
 * Labels de exibição das categorias (detalhe, comprovante, resumo).
 * As 13 do formulário vêm de `views/Reembolso/categorias.ts` (fonte única — sem drift);
 * as legadas mantêm o rótulo simples (sem sufixo "(legado)") e `km` é local desta tela.
 */
export const CATEGORIA_LABEL: Record<string, string> = {
  ...CATEGORIA_LABEL_FORM,
  transporte: 'Transporte',
  outros: 'Outros',
  km: 'Km / Pedágio',
};

/** Texto padrão: comprovante selecionado, upload ao salvar/enviar o formulário. */
export const MSG_COMPROVANTE_UPLOAD_FORMULARIO =
  'O comprovante será enviado ao final do formulário.';

export const MSG_COMPROVANTE_ARQUIVO_MODAL =
  'PDF ou imagem (máx. 10 MB). O envio do arquivo ocorre ao salvar ou enviar a solicitação.';

export type TipoVeiculo = 'combustao' | 'eletrico' | 'hibrido';

export interface TarifaKm {
  tipo: TipoVeiculo;
  label: string;
  valor_km: number;
}

export const TIPO_VEICULO_LABEL: Record<TipoVeiculo, string> = {
  combustao: 'Combustão',
  eletrico: 'Elétrico',
  hibrido: 'Híbrido',
};

export const TARIFAS_KM_PADRAO: TarifaKm[] = [
  { tipo: 'combustao', label: 'Combustão', valor_km: 1.25 },
  { tipo: 'eletrico', label: 'Elétrico', valor_km: 0.46 },
  { tipo: 'hibrido', label: 'Híbrido', valor_km: 0.92 },
];

/** Mapeia rótulo da config (ex.: "Combustão") para enum do backend. */
export function normalizarTipoVeiculo(tipoConfig: string): TipoVeiculo | null {
  const t = tipoConfig.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (t.includes('combust')) return 'combustao';
  if (t.includes('eletr')) return 'eletrico';
  if (t.includes('hibr')) return 'hibrido';
  return null;
}

export function parseTarifasKm(rows: Array<{ tipo: string; valor_km?: number | null }> | undefined): TarifaKm[] {
  if (!rows?.length) return TARIFAS_KM_PADRAO;
  const mapped = rows
    .map((r) => {
      const tipo = normalizarTipoVeiculo(r.tipo);
      if (!tipo) return null;
      return { tipo, label: r.tipo, valor_km: Number(r.valor_km ?? 0) };
    })
    .filter((x): x is TarifaKm => x !== null && x.valor_km > 0);
  return mapped.length ? mapped : TARIFAS_KM_PADRAO;
}

export function tarifaKmPorTipo(tarifas: TarifaKm[], tipo?: TipoVeiculo | null): number {
  const alvo = tipo ?? 'combustao';
  return tarifas.find((t) => t.tipo === alvo)?.valor_km ?? tarifas[0]?.valor_km ?? 1.25;
}

/**
 * Parser de digitação para campos numéricos controlados via `:value`/`@input`
 * (ou `:model-value`/`@update:model-value` do `Input` do design system) — sem
 * `v-model.number`. Revisão final da migração para o Corporate Console (achado
 * crítico): num `<input type="number">`, um valor intermediário inválido (ex.:
 * "120." enquanto o usuário digita "120.5") faz `el.value` voltar `''` (sanitização
 * nativa do browser). Se o handler cai para `Number(v) || 0`, o campo é zerado em
 * silêncio a cada "." digitado — quem tenta cadastrar 120,50 termina com 5.
 *
 * Retorna `null` para "ainda não é um número válido"; o chamador deve then SKIPAR
 * a atribuição (não escrever no model, não marcar dirty) nesse caso — sem mutação
 * reativa o Vue não re-renderiza o input, e o texto que o usuário está digitando
 * permanece intacto até a tecla seguinte completar um número válido. É o mesmo
 * efeito do guard que `v-model.number` fazia antes da migração (o valor reativo
 * ficava `''` durante a digitação e a igualdade impedia o patch do DOM).
 */
export function parseDecimalInput(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Encurta nome de arquivo preservando a extensão. */
export function truncateFileName(name: string, max = 36): string {
  const trimmed = name.trim();
  if (trimmed.length <= max) return trimmed;

  const dot = trimmed.lastIndexOf('.');
  const ext = dot > 0 ? trimmed.slice(dot) : '';
  const baseMax = max - ext.length - 1;
  if (baseMax <= 0) return `${trimmed.slice(0, max - 1)}…`;

  return `${trimmed.slice(0, baseMax)}…${ext}`;
}
